import React from 'react';
import { Layout, Menu, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  FolderOutlined,
  AppstoreOutlined,
  SettingOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  BookOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

const { Sider } = Layout;

// Type definitions
interface SidebarProps {
  onViewChange: (key: string | null) => void;
  activeView: string | null;
}

interface IconWrapperProps {
  icon: React.ReactElement;
  title: string;
}

const Sidebar: React.FC<SidebarProps> = ({ onViewChange, activeView }) => {
  // Handle click with toggle functionality
  const handleMenuClick = ({ key }: { key: string }) => {
    // If clicking the same view that's already active, hide the sidebar
    if (key === activeView) {
      onViewChange(null);
    } else {
      onViewChange(key);
    }
  };
  // Styles
  const siderStyle: React.CSSProperties = {
    width: 60,
    minWidth: 60,
    maxWidth: 60,
  };

  const menuStyle: React.CSSProperties = {
    paddingTop: '10px',
    borderRight: 'none',
  };

  // Icon style for larger icons - specific to these buttons
  const iconStyle: React.CSSProperties = {
    fontSize: '22px', // Make slightly larger since we're being more targeted
  };

  // Custom icon wrapper for proper centering and tooltip coverage
  const IconWrapper: React.FC<IconWrapperProps> = ({ icon, title }) => (
    <Tooltip title={title} placement="right" mouseEnterDelay={0.3}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {React.cloneElement(icon, { style: iconStyle })}
      </div>
    </Tooltip>
  );

  // All menu items with custom icon wrappers
  const items: MenuProps['items'] = [
    {
      key: 'palette',
      icon: (
        <IconWrapper
          icon={<AppstoreOutlined data-testid="sidebar-palette-icon" />}
          title="Steps Palette"
        />
      ),
    },
    {
      key: 'files',
      icon: <IconWrapper icon={<FolderOutlined data-testid="file-browser" />} title="File Browser" />,
    },
    {
      key: 'metadata',
      icon: <IconWrapper icon={<DatabaseOutlined />} title="Metadata" />,
    },
    {
      key: 'dashboard',
      icon: <IconWrapper icon={<BarChartOutlined />} title="Dashboard" />,
    },
    {
      type: 'divider',
      style: {
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        margin: '8px 0',
      },
    },
    {
      key: 'docs',
      icon: <IconWrapper icon={<BookOutlined />} title="Documentation" />,
    },
    {
      key: 'scheduler',
      icon: <IconWrapper icon={<ClockCircleOutlined />} title="Scheduler" />,
    },
    {
      key: 'settings',
      icon: <IconWrapper icon={<SettingOutlined data-testid="sidebar-settings-icon" />} title="Settings" />,
    },
  ];

  return (
    <Sider 
      width={60} 
      theme="dark" 
      collapsible={false} 
      style={{
        ...siderStyle,
        background: 'var(--color-sidebar-background)',
      }}
    >
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={activeView ? [activeView] : []}
        items={items}
        onClick={handleMenuClick}
        style={{
          ...menuStyle,
          background: 'var(--color-sidebar-background)',
        }}
        className="sidebar-menu"
      />
      <style>{`
                .sidebar-menu.ant-menu-inline {
                    border-right: none;
                    background: var(--color-sidebar-background) !important;
                }
                .sidebar-menu .ant-menu-item {
                    display: flex !important;
                    justify-content: center !important;
                    align-items: center !important;
                    padding: 0 !important;
                    height: 40px !important;
                    margin: 0 !important;
                    width: 100% !important;
                    background: var(--color-sidebar-background) !important;
                    color: var(--color-sidebar-text) !important;
                }
                .sidebar-menu .ant-menu-item:hover {
                    background: rgba(255, 255, 255, 0.1) !important;
                    color: var(--color-sidebar-text-hover) !important;
                }
                .sidebar-menu .ant-menu-item-selected {
                    background: var(--color-primary) !important;
                    color: var(--color-sidebar-text-active) !important;
                }
                .sidebar-menu .ant-menu-title-content {
                    display: flex !important;
                    justify-content: center !important;
                    align-items: center !important;
                    width: 100% !important;
                }
                .sidebar-menu .ant-menu-item::after {
                    display: none !important;
                }
            `}</style>
    </Sider>
  );
};

export default Sidebar;
